/**
 * Scores Service
 *
 * API endpoints for investment scoring operations
 *
 * Data Model:
 * - Scores (admin): Template scores in lookup table
 * - InvestmentScores: Cloned scores per investment with score_override
 * - ScoreCategories: Categories with selection_type (S=single, M=multiple)
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { InvestmentScore, Score, ScoreCategory } from '@medsuite/models';

/**
 * Score with category info (from available scores endpoint)
 */
export interface ScoreWithCategory extends Score {
  score_category_name: string;
  selection_type: 'S' | 'M';
}

/**
 * Category with available scores
 */
export interface CategoryWithScores extends ScoreCategory {
  scores: Score[];
}

/**
 * Investment score grouped by category
 */
export interface InvestmentScoreGrouped {
  score_category_id: number;
  score_category_name: string;
  selection_type: 'S' | 'M';
  investment_scores: InvestmentScore[];
}

/**
 * Score summary by dimension (from getScoreSummary)
 */
export interface ScoreSummary {
  dimension_totals: {
    security_score: number;
    growth_score: number;
    efficiency_score: number;
    infrastructure_score: number;
    clinical_score: number;
    score_override: number;
    strategy_allocation: number;
    points: number;
  };
  total_scores_selected: number;
}

/**
 * Category scores breakdown
 */
export interface CategoryScores {
  security_score: number;
  growth_score: number;
  efficiency_score: number;
  infrastructure_score: number;
  clinical_score: number;
}

/**
 * Composite score object (from getCompositeScore)
 */
export interface CompositeScoreResult {
  total_score: number;
  category_scores: CategoryScores;
  weighted_score: number;
  is_overridden: boolean;
  strategy_count: number;
}

/**
 * Investment scores response
 */
export interface InvestmentScoresResponse {
  investment_id: number;
  composite_score: CompositeScoreResult;
  categories: InvestmentScoreGrouped[];
  summary: ScoreSummary;
}

/**
 * Available scores response
 */
export interface AvailableScoresResponse {
  investment_id: number;
  categories: CategoryWithScores[];
}

/**
 * Single score add response
 */
export interface AddScoreResponse {
  investment_score: InvestmentScore;
  composite_score: number;
}

/**
 * Batch scores add response
 */
export interface AddScoresBatchResponse {
  investment_scores: InvestmentScore[];
  composite_score: number;
}

/**
 * Score update response
 */
export interface UpdateScoreResponse {
  investment_score: InvestmentScore;
  composite_score: number;
}

/**
 * Score delete response
 */
export interface DeleteScoreResponse {
  message: string;
  deleted_investment_score_id: number;
  composite_score: number;
}

/**
 * Scores Service
 */
class ScoresService {
  /**
   * Get investment scores grouped by category
   */
  async getInvestmentScores(investmentId: number): Promise<ApiResponse<InvestmentScoresResponse>> {
    return apiClient.get<InvestmentScoresResponse>(`/investments/${investmentId}/scores`);
  }

  /**
   * Get all available scores (from admin table) grouped by category
   */
  async getAvailableScores(investmentId: number): Promise<ApiResponse<AvailableScoresResponse>> {
    return apiClient.get<AvailableScoresResponse>(`/investments/${investmentId}/scores/available`);
  }

  /**
   * Add a score to an investment (clone from admin Scores table)
   */
  async addScore(investmentId: number, scoreId: number): Promise<ApiResponse<AddScoreResponse>> {
    return apiClient.post<AddScoreResponse>(`/investments/${investmentId}/scores`, {
      score_id: scoreId,
    });
  }

  /**
   * Add multiple scores to an investment (for multi-selection categories)
   */
  async addScoresBatch(
    investmentId: number,
    scoreIds: number[]
  ): Promise<ApiResponse<AddScoresBatchResponse>> {
    return apiClient.post<AddScoresBatchResponse>(`/investments/${investmentId}/scores/batch`, {
      score_ids: scoreIds,
    });
  }

  /**
   * Replace a score in a category (for single-selection categories)
   */
  async replaceCategoryScore(
    investmentId: number,
    scoreCategoryId: number,
    scoreId: number
  ): Promise<ApiResponse<UpdateScoreResponse>> {
    return apiClient.put<UpdateScoreResponse>(`/investments/${investmentId}/scores/replace`, {
      score_category_id: scoreCategoryId,
      score_id: scoreId,
    });
  }

  /**
   * Update the score_override for an investment score
   */
  async updateScoreOverride(
    investmentId: number,
    investmentScoreId: number,
    scoreOverride: number
  ): Promise<ApiResponse<UpdateScoreResponse>> {
    return apiClient.put<UpdateScoreResponse>(
      `/investments/${investmentId}/scores/${investmentScoreId}`,
      {
        score_override: scoreOverride,
      }
    );
  }

  /**
   * Remove a score from an investment
   */
  async removeScore(
    investmentId: number,
    investmentScoreId: number
  ): Promise<ApiResponse<DeleteScoreResponse>> {
    return apiClient.delete<DeleteScoreResponse>(
      `/investments/${investmentId}/scores/${investmentScoreId}`
    );
  }

  /**
   * Clear all scores for an investment
   */
  async clearAllScores(
    investmentId: number
  ): Promise<ApiResponse<{ message: string; investment_id: number }>> {
    return apiClient.delete(`/investments/${investmentId}/scores`);
  }
}

export const scoresService = new ScoresService();
